import { COUNTRY_BY_SALESORG, isFutureTech } from '@/libs/constants'
import {
  AllLanguages,
  DEFAULT_LANGUAGES,
  SUPPORTED_LANGUAGES,
  SupportedLocales,
} from '@/libs/constants/lang'

import i18n from '../../i18n'

const LOCALE_SEPARATOR_PATTERN = /[-_]/

export const ChangeLang = (lang) => {
  i18n.changeLanguage(lang)
  localStorage.setItem('lng', lang)

  const event = new Event('changeLang')
  event.value = lang
  event.key = 'lng'
  document.dispatchEvent(event)
}

export function handleChangeLanguage(locale, countryId) {
  const getCorrectLocale = () => {
    const language = locale.slice(0, 2)
    if (isFutureTech(countryId) && language === 'de') {
      return 'de-FT'
    }
    const country = COUNTRY_BY_SALESORG[countryId]
    const newLocale = `${language}${country ? `-${country}` : ''}`
    if (SUPPORTED_LANGUAGES.includes(newLocale)) {
      return newLocale
    }
    return language
  }

  ChangeLang(getCorrectLocale())
}

export const getLanguage = (lang) => lang?.replace(/[-_][a-z]+$/i, '')

export const getLanguageFromLocalStorage = () =>
  localStorage.getItem('lng') || DEFAULT_LANGUAGES[0].value

export const checkValidCultureFormat = (culture) => /^[a-z]{2}(?:[-_][A-Z]{2})?$/.test(culture)

/**
 * Normalizes a culture string to the expected format (e.g., "en" or "nl-NL")
 * @param {string} culture - The culture to normalize
 * @returns {string} Normalized culture (e.g., "nl-nl" -> "nl-NL", "DE-at" -> "de-AT")
 * @example
 * normalizeCulture("nl-nl") // Returns "nl-NL"
 * normalizeCulture("DE-at") // Returns "de-AT"
 * normalizeCulture("en") // Returns "en"
 * normalizeCulture("EN") // Returns "en"
 */
const normalizeCulture = (culture) => {
  const defaultLocale = DEFAULT_LANGUAGES[0].value

  if (!culture || typeof culture !== 'string') {
    return defaultLocale
  }

  if (LOCALE_SEPARATOR_PATTERN.test(culture)) {
    const parts = culture.split(LOCALE_SEPARATOR_PATTERN)

    if (parts.length === 2 && parts[0] && parts[1]) {
      return `${parts[0].toLowerCase()}-${parts[1].toUpperCase()}`
    }

    return defaultLocale
  }

  const normalizedLangCode = culture.toLowerCase()

  if (AllLanguages.includes(normalizedLangCode) || SupportedLocales.includes(normalizedLangCode)) {
    return normalizedLangCode
  }

  return defaultLocale
}

export const getI18nLanguage = () => {
  const currentLang = i18n.language

  // If current language is already in valid format, return it directly
  if (checkValidCultureFormat(currentLang)) {
    return currentLang
  }

  // Otherwise, normalize the culture to handle case variations or invalid formats
  const normalized = normalizeCulture(currentLang)

  if (checkValidCultureFormat(normalized)) {
    return normalized
  }

  return DEFAULT_LANGUAGES[0].value
}

/**
 * Check whether the culture is supported or unsupported
 * @param   {string} culture  The language to check
 * @returns {Bool}   result   Is the culture supported
 */
export const checkIsLanguageSupported = (culture) => {
  if (!culture) {
    return false
  }

  return SUPPORTED_LANGUAGES.includes(culture)
}

/**
 * Removed supported language codes prefix in the URL
 * @param {string} currentUrl The URL path
 * @returns {string} output is a removed lang code URL
 */
export const removeLangCodesPrefix = (currentUrl) => {
  const i18nLanguage = getI18nLanguage()

  /* Input examples:
   * /en/product/1.234-567.8
   * /de-AT/product/1.234-567.8
   * Output:
   * /product/1.234-567.8
   */
  return currentUrl
    .split('/')
    .filter((e) => e !== i18nLanguage)
    .join('/')
}

/**
 * Append culture to a link
 * @param   {string} link The link to navigate to
 * @returns {string}      The link contains culture
 */
export const linkGenerator = (link) => {
  const paths = link?.split('/')?.filter((path) => path !== '')
  const i18nLanguage = getI18nLanguage()

  /**
   * Example: ["contact-form"], ["vouchers"]
   */
  if (paths && paths.length > 0) {
    const culture = paths[0]

    /**
     * Check if the URL already contains the culture
     * then return it without adding the culture
     * Example:
     * - localhost:3000/fr-CH/category/11023648/subcategory/11023648
     */
    const isCultureFormatValid = checkValidCultureFormat(culture)
    const isCultureAlreadyInUrl = checkIsLanguageSupported(culture)

    if (isCultureFormatValid && isCultureAlreadyInUrl) {
      return link
    }
  }

  const appendedCultureUrl = `/${i18nLanguage}${link}`

  return appendedCultureUrl
}

export const getCultureFromRegistrationLink = (link) => {
  const paths = link?.split('/')?.filter((path) => path)

  const [lang, country] = paths.slice(-2)

  const culture = [lang, country].join('-')
  if (checkValidCultureFormat(culture) && checkIsLanguageSupported(culture)) {
    return culture
  }
  return ''
}

export function cleanLanguage(language) {
  // To remove countryId attached with language
  // Example: en-UK => en, de-CH => de
  let lang = language

  if (lang?.length > 2) {
    lang = language.slice(0, 2)
  }

  return lang
}

/**
 * Removes numeric characters from a locale string.
 *
 * @param {string} locale - The locale string that may contain numbers (e.g., "de-CH10")
 * @returns {string} The cleaned locale string without numbers (e.g., "de-CH")
 *
 * @example
 * // Returns "de-CH"
 * cleanLocale("de-CH10");
 *
 * @example
 * // Returns "en-US"
 * cleanLocale("en-US123");
 */
export function cleanLocale(locale) {
  if (!locale || typeof locale !== 'string') {
    return ''
  }

  return locale.replaceAll(/\d+/g, '')
}
